<template>
  <SmartblockList
    class="sdv-activation-smartblock-list"
    :context="context"
    :template="template"
    :block-style="blockStyle"
    @analytics="listeners.onAnalytics"
    @action="listeners.onAction"
    @call-event="listeners.onSmartblockEvent"
  />
</template>

<script lang="ts">
/**
 * Translates activation template into smartblock list.
 */
import { ExperienceActivationContext, ExperienceActivationInstance } from 'sd-types/app/experience/activation'
import { ActivationTemplate } from 'sd-types/models/activation'
import { computed, defineComponent, PropType, unref, toRefs, onMounted } from 'vue-demi'
import SmartblockList from './Smartblock/SmartblockList.vue'
import { useSmartblockOptions } from '../composables/internals/use-smartblock-option'
import { defineActivationListEmits, useActivationListEvents } from '../composables/use-activation-list-events'

export default defineComponent({
  name: 'ActivationSmartblockList',
  components: {
    SmartblockList
  },
  props: {
    activation: {
      type: Object as PropType<ExperienceActivationInstance>,
      required: true
    },
    context: {
      type: Object as PropType<ExperienceActivationContext>,
      default: () => ({})
    },
    preview: {
      type: Boolean,
      default: false
    }
  },
  emits: {
    ...defineActivationListEmits()
  },
  setup (props, context) {
    const options = useSmartblockOptions()
    /**
     * Switch template between details and preview.
     */
    const template = computed<ActivationTemplate[]>((): ActivationTemplate[] => {
      if (props.preview) {
        return props.activation.preview?.template ?? []
      }
      return props.activation.details?.template ?? props.activation.template ?? []
    })

    /**
     * Style to be applied to each block in the list.
     */
    const blockStyle = computed(() => {
      if (!props.preview) return props.activation?.details?.blockStyle ?? unref(options?.defaultBlockStyle) ?? {}
      return unref(options?.defaultBlockStyle) ?? {}
    })

    const { activation, context: activationContext } = toRefs(props)
    /**
     * Group of smartblock event listeners.
     */
    const listeners = useActivationListEvents(activation, activationContext, context)

    /**
     * Emit needed analytics
     * TODO: analytics reference point - change this when proper analytics functionality is supported
     */
    onMounted(() => listeners.onAnalytics({ event: 'display' }))
    return {
      blockStyle,
      template,
      listeners
    }
  }
})
</script>
